
import { Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "@/components/ErrorBoundary";
import LoadingFallback from "@/components/LoadingFallback";
import { AuthProvider } from "@/context/AuthContext";
import { useErrorTracking } from "@/hooks/use-error-tracking";
import AuditLogsPage from "./pages/admin/AuditLogsPage";

// Public pages
import Index from "./pages/Index";
import Collectors from "./pages/Collectors";
import HelpCenter from "./pages/HelpCenter";
import About from "./pages/About";
import Contact from "./pages/Contact";
import Auth from "./pages/Auth";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import PaymentSuccess from "./pages/PaymentSuccess";
import LegalLookup from "./pages/LegalLookup";
import AcceptInvitation from "./pages/AcceptInvitation";
import AcceptTransfer from "./pages/AcceptTransfer";
import OrganizationSetupPage from "./pages/OrganizationSetup";
import WaitingForInvitation from "./pages/WaitingForInvitation";
import EmailVerified from "./pages/EmailVerified";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import ServiceLevelAgreement from "./pages/ServiceLevelAgreement";
import PublicProfile from "./pages/PublicProfile";

// Admin layout
import AdminLayout from "./components/layout/AdminLayout";

// Lazy loaded admin pages
import {
  LazyDashboard,
  LazyFacilitiesPage,
  LazyFacilityDetailsPage,
  LazyRegulationsPage,
  LazyAnimalsPage,
  LazyAnimalDetailsPage,
  LazyAnimalGroupsPage,
  LazyAnimalGroupDetailsPage,
  LazyPermitsPage,
  LazyVetRecordsPage,
  LazyFormularyPage,
  LazyInspectionsPage,
  LazyTransportTripsPage,
  LazyLegalLookupPage,
  LazyBillingPage,
  LazyUsersPage,
  LazyOrganizationsPage,
  LazySiteSettingsPage,
  LazyStripeSetupTest,
  LazyStripeProductSetup,
  LazyStripeCollectorSync,
  LazyStripeDiagnostics,
  LazyEnrichmentDietPlansPage,
  LazySecurityDashboard,
  LazyFeaturesPage,
  LazyCollectorDashboard,
  LazyCollectorAnimalsPage,
  LazyCollectorEnclosuresPage,
  LazyEnclosuresPage,
  LazyExportsPage,
  LazyTransfersPage,
  LazyLegalityAuditPage,
} from "./components/optimized/LazyRoutes";

// Collector Pages
import {
  LazyPublicProfileSettings,
  LazyUiSettings,
  LazyCollectorAnalytics,
  LazyCollectorAnimalDetailsPage,
} from "./components/optimized/LazyRoutesV2";

// Search Results
import SearchResults from "./pages/admin/SearchResults";

// Protected route wrapper
import ProtectedRoute from "./components/auth/ProtectedRoute";
import AdminGuard from "./components/auth/AdminGuard";
import CollectorGuard from "./components/auth/CollectorGuard";
import { CollectorAccessGuard } from "./components/auth/CollectorAccessGuard";
import { TransportAccessGuard } from "./components/auth/TransportAccessGuard";

// Transport pages
import TransportDirectory from "./pages/TransportDirectory";
import TransportDirectoryAdmin from "./pages/admin/TransportDirectoryAdmin";

// Optimize React Query configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      retry: (failureCount, error: any) => {
        // Don't retry on 4xx errors
        if (error?.status >= 400 && error?.status < 500) {
          return false;
        }
        return failureCount < 2;
      },
    },
  },
});

const AppContent = () => {
  // Initialize global error tracking
  useErrorTracking();
  
  return (
    <BrowserRouter>
            <Routes>
            {/* Public routes */}
            <Route path="/" element={<Index />} />
            <Route path="/pricing" element={<Navigate to="/#pricing" replace />} />
            <Route path="/collectors" element={<Collectors />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/features" element={
              <Suspense fallback={<LoadingFallback />}>
                <LazyFeaturesPage />
              </Suspense>
            } />
            <Route path="/about" element={<About />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/auth" element={<Auth />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/legal-lookup" element={<LegalLookup />} />
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/accept-transfer" element={<AcceptTransfer />} />
            <Route path="/organization-setup" element={<OrganizationSetupPage />} />
            <Route path="/waiting-for-invitation" element={<WaitingForInvitation />} />
            <Route path="/email-verified" element={<EmailVerified />} />
            <Route path="/subscription-success" element={<SubscriptionSuccess />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/sla" element={<ServiceLevelAgreement />} />
            
            {/* Public collector profiles */}
            <Route path="/u/:username" element={<PublicProfile />} />
            
            {/* Public transport directory */}
            <Route path="/transport/:stateCode" element={<TransportDirectory />} />
            
            <Route path="/payment-success" element={<PaymentSuccess />} />

            {/* Protected admin routes */}
            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  <AdminLayout />
                </ProtectedRoute>
              }
            >
              <Route index element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyDashboard />
                </Suspense>
              } />
              <Route path="dashboard" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyDashboard />
                </Suspense>
              } />
              <Route path="facilities" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyFacilitiesPage />
                </Suspense>
              } />
              <Route path="facilities/:id" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyFacilityDetailsPage />
                </Suspense>
              } />
              <Route path="enclosures" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyEnclosuresPage />
                </Suspense>
              } />
              <Route path="animals" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyAnimalsPage />
                </Suspense>
              } />
              <Route path="animals/:id" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyAnimalDetailsPage />
                </Suspense>
              } />
              <Route path="animal-groups" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyAnimalGroupsPage />
                </Suspense>
              } />
              <Route path="animal-groups/:id" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyAnimalGroupDetailsPage />
                </Suspense>
              } />
              <Route path="permits" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyPermitsPage />
                </Suspense>
              } />
              <Route path="vet-records" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyVetRecordsPage />
                </Suspense>
              } />
              <Route path="formulary" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyFormularyPage />
                </Suspense>
              } />
              <Route path="enrichment-diet-plans" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyEnrichmentDietPlansPage />
                </Suspense>
              } />
              <Route path="inspections" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyInspectionsPage />
                </Suspense>
              } />
              <Route path="transport" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyTransportTripsPage />
                </Suspense>
              } />
              <Route path="legal-lookup" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyLegalLookupPage />
                </Suspense>
              } />
              <Route path="transfers" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyTransfersPage />
                </Suspense>
              } />
              <Route path="regulations" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyRegulationsPage />
                </Suspense>
              } />
              <Route path="billing" element={
                <Suspense fallback={<LoadingFallback />}>
                  <LazyBillingPage />
                </Suspense>
              } />
              <Route 
                path="users" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyUsersPage />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="security" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazySecurityDashboard />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route path="search" element={<SearchResults />} />
              <Route 
                path="audit"
                element={
                  <AdminGuard>
                    <AuditLogsPage />
                  </AdminGuard>
                } 
              />
              <Route 
                path="organizations"
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyOrganizationsPage />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="site-settings" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazySiteSettingsPage />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="legality-audit" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyLegalityAuditPage />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="stripe-setup-test" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyStripeSetupTest />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="stripe-product-setup" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyStripeProductSetup />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="stripe-collector-sync" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyStripeCollectorSync />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              <Route 
                path="stripe-diagnostics" 
                element={
                  <AdminGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyStripeDiagnostics />
                    </Suspense>
                  </AdminGuard>
                } 
              />
              
              {/* Collector Tools - nested under admin */}
              <Route 
                path="collector/dashboard" 
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyCollectorDashboard />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              
              {/* Legacy V2 dashboard redirects */}
              <Route 
                path="collector/dashboard-v2" 
                element={<Navigate to="/admin/collector/dashboard" replace />}
              />
              <Route 
                path="collector/dashboard-beta" 
                element={<Navigate to="/admin/collector/dashboard" replace />}
              />
              <Route 
                path="collector-v2" 
                element={<Navigate to="/admin/collector/dashboard" replace />}
              />
              
              <Route 
                path="collector/animals" 
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyCollectorAnimalsPage />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              <Route 
                path="collector/animals/:id" 
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyCollectorAnimalDetailsPage />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              <Route 
                path="collector/enclosures"
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyCollectorEnclosuresPage />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              
              {/* Collector Analytics - Unified */}
              <Route 
                path="collector/analytics" 
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyCollectorAnalytics />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              
              {/* Legacy V2 analytics redirect */}
              <Route 
                path="collector/v2/analytics" 
                element={<Navigate to="/admin/collector/analytics" replace />}
              />
              
              {/* Public Profile - Plan-Aware (Collector + Facility) */}
              <Route 
                path="public-profile" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyPublicProfileSettings />
                    </Suspense>
                  </ProtectedRoute>
                } 
              />
              
              {/* Legacy V2 public profile redirect */}
              <Route 
                path="collector/v2/public-profile" 
                element={<Navigate to="/admin/public-profile" replace />}
              />
              
              <Route 
                path="settings/ui" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyUiSettings />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="exports" 
                element={
                  <ProtectedRoute>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyExportsPage />
                    </Suspense>
                  </ProtectedRoute>
                }
              />
              <Route 
                path="collector/exports" 
                element={
                  <CollectorAccessGuard>
                    <Suspense fallback={<LoadingFallback />}>
                      <LazyExportsPage />
                    </Suspense>
                  </CollectorAccessGuard>
                } 
              />
              
              {/* Transport Directory Admin */}
              <Route 
                path="transport-directory" 
                element={
                  <TransportAccessGuard>
                    <TransportDirectoryAdmin />
                  </TransportAccessGuard>
                } 
              />
            </Route>

            {/* 404 fallback */}
            <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
  );
};

const App = () => (
  <ErrorBoundary>
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <AppContent />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  </ErrorBoundary>
);

export default App;
